import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import api from '@services/api';
import { DeviceTotalChart } from '@services/types';
import { Card } from 'antd';

import moment from 'moment';
import ChartWrapper from 'component/ChartWrapper';

const options: ApexOptions = {
  chart: {
    toolbar: {
      show: true,
      offsetX: 0,
      offsetY: 0,
      export: {
        csv: {
          filename: 'deviceSince',
          columnDelimiter: ',',
          headerCategory: 'category',
          headerValue: 'value',
        },
        svg: {
          filename: 'deviceSince',
        },
        png: {
          filename: 'deviceSince',
        },
      },
      autoSelected: 'zoom',
    },
    zoom: {
      enabled: true,
      type: 'x',
      autoScaleYaxis: false,
      zoomedArea: {
        fill: {
          color: '#90CAF9',
          opacity: 0.4,
        },
        stroke: {
          color: '#0D47A1',
          opacity: 0.4,
          width: 1,
        },
      },
    },
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
      dataLabels: {
        position: 'center',
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  title: {
    text: 'Total number of devices registered per day',
    align: 'center',
    margin: 20,
    offsetX: 20,
    offsetY: -10,
    floating: true,
    style: {
      fontSize: '15px',
      fontWeight: '500',
      fontFamily: 'Montserrat',
      color: '#29323FDD',
    },
  },
  xaxis: {
    categories: [],
    type: 'datetime',

    tickAmount: 'dataPoints',
    position: 'bottom',
    min: undefined,
    max: undefined,
    axisBorder: {
      show: true,
    },
    labels: {
      hideOverlappingLabels: true,
      showDuplicates: false,
      show: true,
      trim: true,
      maxHeight: 100,
      style: {
        fontSize: '10px',
        fontFamily: 'Montserrat',
        fontWeight: 400,
      },
      datetimeUTC: false,
      datetimeFormatter: {
        year: 'yyyy',
        month: "MMM 'yy",
        day: 'dd MMM',
      },
    },
  },
  yaxis: {
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      show: true,
    },
  },
  noData: {
    text: 'Loading...',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: '#0080FFDD',
      fontSize: '14px',
      fontFamily: 'Montserrat',
    },
  },
  tooltip: {
    enabled: true,
    enabledOnSeries: undefined,
    shared: true,
    followCursor: false,
    intersect: false,
    inverseOrder: false,
    custom: undefined,
    fillSeriesColor: false,
    theme: 'dark',
    style: {
      fontSize: '14px',
      fontFamily: 'Montserrat',
    },
    onDatasetHover: {
      highlightDataSeries: false,
    },
  },
  series: [
    {
      name: 'Devices',
      data: [],
    },
  ],
};
const secondOptions: ApexOptions = {
  chart: {
    toolbar: {
      show: true,
      offsetX: 0,
      offsetY: 0,
      export: {
        csv: {
          filename: 'deviceConnectionNumber',
          columnDelimiter: ',',
          headerCategory: 'category',
          headerValue: 'value',
        },
        svg: {
          filename: 'deviceConnectionNumber',
        },
        png: {
          filename: 'deviceConnectionNumber',
        },
      },
      autoSelected: 'zoom',
    },
    zoom: {
      enabled: true,
      type: 'x',
      autoScaleYaxis: false,
      zoomedArea: {
        fill: {
          color: '#0083fe',
          opacity: 0,
        },
        stroke: {
          color: '#0083fe',
          opacity: 0.5,
          width: 1.5,
        },
      },
    },
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
      dataLabels: {
        position: 'center',
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  title: {
    text: 'Total number of devices registered per week',
    align: 'center',
    margin: 20,
    offsetX: 20,
    offsetY: -10,
    floating: true,
    style: {
      fontSize: '15px',
      fontWeight: '500',
      fontFamily: 'Montserrat',
      color: '#29323FDD',
    },
  },
  xaxis: {
    categories: [],
    type: 'category',
    tickAmount: 'dataPoints',
    position: 'bottom',
    min: undefined,
    max: undefined,
    axisBorder: {
      show: true,
    },
    labels: {
      rotate: -75,
      show: true,
      trim: true,
      hideOverlappingLabels: true,
      showDuplicates: false,
      maxHeight: 150,
      style: {
        fontSize: '10px',
        fontFamily: 'Montserrat',
        fontWeight: 400,
      },
    },
  },
  yaxis: {
    labels: {
      show: true,
    },
  },
  tooltip: {
    enabled: true,
    enabledOnSeries: undefined,
    shared: true,
    followCursor: true,
    intersect: false,
    inverseOrder: false,
    fillSeriesColor: false,
    custom: undefined,
    theme: 'dark',
    style: {
      fontSize: '14px',
      fontFamily: 'Montserrat',
    },
    onDatasetHover: {
      highlightDataSeries: false,
    },
  },
  noData: {
    text: 'Loading...',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: {
      color: '#0080FFDD',
      fontSize: '25px',
      fontFamily: 'Montserrat',
    },
  },
  series: [
    {
      name: 'Device Connections',
      data: [],
    },
  ],
};

interface Props {
  selectDevice?: DeviceTotalChart;
}

const DeviceNumber: React.FC<Props> = ({ selectDevice }) => {
  const [chart, setChart] = React.useState<ApexOptions>(options);
  const [secondChart, setSecondChart] = React.useState<ApexOptions>(secondOptions);

  React.useEffect(() => {
    if (selectDevice?.deviceTypeId && selectDevice?.count) {
      Promise.all([
        api.reports.getDeviceNumber({ deviceTypeId: selectDevice?.deviceTypeId, nbTotal: selectDevice?.count }),
        api.reports.getReportByWeek({ deviceTypeId: selectDevice.deviceTypeId }),
      ]).then(([visit, connect]) => {
        const newChartData = visit;
        const newChartSecondData = connect;
        if (newChartData) {
          const dataObj = Object.values(newChartData.elements || []);
          const count = dataObj.map(({ count }) => count);
          const dataTime = dataObj.map((date) => moment(date.date).format('YYYY-MM-DD'));
          if (dataTime.length) {
            setChart({
              ...chart,
              series: [
                {
                  ...chart.series,
                  data: count,
                },
              ],
              xaxis: {
                ...chart.xaxis,
                categories: dataTime,
              },
            });
          }
        }
        if (newChartSecondData) {
          const countNumber = newChartSecondData.map((count) => count.count);
          const dataTimeNumber = newChartSecondData?.map(({ week }) => moment(week).format('YYYY-MM-DD'));
          if (dataTimeNumber.length) {
            setSecondChart({
              ...secondChart,
              series: [
                {
                  ...secondChart.series,
                  data: countNumber,
                },
              ],
              xaxis: {
                ...secondChart.xaxis,
                categories: dataTimeNumber,
              },
            });
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectDevice]);

  return (
    <Card
      title={`Device Type ${selectDevice?.deviceTypeName} : ${selectDevice?.count}`}
      style={{ marginTop: '20px', overflow: 'hidden' }}
      bodyStyle={{ display: 'grid', gridGap: '50px' }}
    >
      <ChartWrapper>
        <ReactApexChart options={chart} series={chart.series} type="line" height={300} />
      </ChartWrapper>
      <ChartWrapper>
        <ReactApexChart options={secondChart} series={secondChart.series} type="bar" height={300} />
      </ChartWrapper>
    </Card>
  );
};
export default React.memo(DeviceNumber);
